import ReportTab from "@/pages/guest/tabs/ReportTab.vue";
import AdminReportTab from "@/pages/guest/tabs/admin/AdminReportTab.vue";
import reportService from "@/services/report";
import hasGuestOrdersScreen from "../hasGuestOrdersScreen";

/**
 * Učitavanje za restoran (i nalog "report_guest"): isto što i stari tab (ReportTab), uz stanje
 * greške i "tiho" osvežavanje (uživo) bez skeleta.
 */
const restaurantLoader = {
  methods: {
    loadReportData({ silent = false } = {}) {
      if (!this.dateRange || this.dateRange.length < 2) {
        return Promise.resolve();
      }
      if (!silent) {
        this.isLoading = true;
      }
      this.failed = false;
      return reportService
        .getNoContractDelivery(this.$store.state.restaurant.id, {
          date: this.dateRange[0],
          date2: this.dateRange[1],
        })
        .then((response) => {
          this.reportData = response.data;
          this.markLoaded();
        })
        .catch((error) => {
          console.error("Error loading report data:", error);
          this.failed = true;
          if (!silent) {
            this.reportData = {};
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};

/**
 * Učitavanje za admina (više restorana), isto što i stari admin tab. Stari tab učitava tek kad se
 * promeni izbor restorana, pa se ovde prvo učitavanje radi pri otvaranju.
 */
const adminLoader = {
  mounted() {
    this.loadReportData();
  },
  methods: {
    loadReportData({ silent = false } = {}) {
      if (!this.restaurantIds || !this.dateRange || this.dateRange.length !== 2) {
        return Promise.resolve();
      }
      if (!silent) {
        this.isLoading = true;
      }
      this.failed = false;
      return reportService
        .getCompanyMultipleNoContract({
          date: this.dateRange[0],
          date2: this.dateRange[1],
          restaurants: this.restaurantIds,
        })
        .then((response) => {
          this.reportData = response.data;
          // firme iz narudžbi: idu u podnožje štampe i Excela, kao u starom tabu
          const companies = new Set();
          const orders = (this.reportData && this.reportData.orders) || {};
          Object.keys(orders).forEach((date) => {
            orders[date].forEach((user) => {
              if (user.company_name) {
                companies.add(user.company_name);
              }
            });
          });
          this.companies = Array.from(companies).sort();
          this.markLoaded();
        })
        .catch((error) => {
          console.error("Error loading report data:", error);
          this.failed = true;
          if (!silent) {
            this.reportData = {};
            this.companies = [];
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};

/**
 * Ekran "Narudžbe kupaca": podaci, promena statusa, štampa i Excel dolaze iz starog taba (ReportTab za
 * restoran, AdminReportTab za admina), a prikaz (`view`) je novi: tabela na računaru, kartice na telefonu.
 * Prikaz dobija ista svojstva i javlja iste događaje, pa je zamenljiv.
 */
export const createOrdersScreen = ({ name, admin = false, view, panel = null }) => ({
  name,
  panelComponent: panel,
  extends: admin ? AdminReportTab : ReportTab,
  mixins: [hasGuestOrdersScreen, admin ? adminLoader : restaurantLoader],
  render(h) {
    return h(view, {
      props: {
        days: this.filteredData,
        loading: this.isLoading,
        failed: this.failed,
        hasOrders: this.hasOrders,
        statusCounts: this.statusCounts,
        total: this.searchedOrders.length,
        status: this.status,
        delivery: this.delivery,
        deliveryCounts: this.deliveryCounts,
        changingId: this.changingStatusOrderId,
        activeId: this.activeId,
        isAdmin: this.isAdmin,
        search: this.searchQuery,
        live: this.live,
        liveText: this.liveText,
      },
      on: {
        "update:status": (value) => this.$emit("update:status", value),
        "update:delivery": (value) => this.$emit("update:delivery", value),
        "update:live": (value) => {
          this.live = value;
        },
        open: ({ order, guest, event }) => this.openOrder(order, guest, event),
        "change-status": ({ order, guest, newStatus }) => this.changeOrderStatus(order, guest, newStatus),
        retry: () => this.loadReportData(),
        toast: (...args) => this.$emit("toast", ...args),
      },
    });
  },
});
